import { useCallback, useEffect, useState } from "react";
import type { ComponentType } from "react";
import { AnimatePresence, motion } from "motion/react";
import { BarChart3, FileSearch, Gauge, Keyboard, Map, Search } from "lucide-react";
import type { SlideDef, SlideProps } from "@/engine/types";
import { ExplainerPanel, Reveal, SlideShell, useAccent } from "@/ui";
import type { Callout } from "./components/gscData";
import { PerformanceTab } from "./components/PerformanceTab";
import { IndexingTab } from "./components/IndexingTab";
import { VitalsTab } from "./components/VitalsTab";
import { SitemapsTab } from "./components/SitemapsTab";

interface IconProps {
  className?: string;
  style?: React.CSSProperties;
}

const TABS = [
  { id: "performance", label: "Performance", icon: BarChart3 },
  { id: "indexing", label: "Indexing", icon: FileSearch },
  { id: "vitals", label: "Core Web Vitals", icon: Gauge },
  { id: "sitemaps", label: "Sitemaps", icon: Map },
] as const satisfies readonly { id: string; label: string; icon: ComponentType<IconProps> }[];

type TabId = (typeof TABS)[number]["id"];

function GscBody({ active }: SlideProps) {
  const [from, to] = useAccent();
  const [tab, setTab] = useState<TabId>("performance");
  const [panelOpen, setPanelOpen] = useState(false);
  /** Kept during the panel's exit animation so its content doesn't blank. */
  const [callout, setCallout] = useState<Callout | null>(null);
  const [visited, setVisited] = useState<ReadonlySet<string>>(new Set());

  const openCallout = useCallback((c: Callout) => {
    setCallout(c);
    setPanelOpen(true);
    setVisited((prev) => new Set(prev).add(c.id));
  }, []);

  const switchTab = useCallback((next: TabId) => {
    setTab(next);
    setPanelOpen(false);
  }, []);

  /* Keys 1–4 switch reports. preventDefault only for keys we consume. */
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = ["1", "2", "3", "4"].indexOf(e.key);
      if (idx === -1) return;
      const target = TABS[idx];
      if (!target) return;
      e.preventDefault();
      switchTab(target.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, switchTab]);

  /* Close the explainer when the overview opens / slide deactivates. */
  useEffect(() => {
    if (!active) setPanelOpen(false);
  }, [active]);

  const pinProps = {
    openId: panelOpen ? (callout?.id ?? null) : null,
    visited,
    onOpen: openCallout,
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <Reveal active={active} className="flex h-full min-h-0 flex-col">
        <div className="glass flex h-full min-h-0 flex-col overflow-hidden">
          {/* Top bar */}
          <div className="flex shrink-0 items-center gap-6 border-b border-white/10 px-6 py-3">
            <div className="flex items-center gap-3">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{ background: `linear-gradient(135deg, ${from}33, ${to}33)` }}
              >
                <Search className="h-5 w-5" style={{ color: from }} />
              </span>
              <span className="font-display text-2xl font-semibold text-white/90">
                Search Console
              </span>
            </div>
            <span className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-1.5">
              <span className="font-mono text-xl text-white/85">yoursite.example</span>
              <span className="h-1.5 w-1.5 rounded-full bg-white/30" />
              <span className="text-xl font-medium uppercase tracking-wider text-accent-amber">
                all data invented
              </span>
            </span>
            <span className="ml-auto flex items-center gap-2.5 text-xl text-white/45">
              <Keyboard className="h-6 w-6" />
              1–4 switch reports
            </span>
          </div>

          <div className="flex min-h-0 flex-1">
            {/* Left rail */}
            <nav className="flex w-72 shrink-0 flex-col gap-1.5 border-r border-white/10 p-4">
              {TABS.map((t, i) => {
                const isActive = tab === t.id;
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => switchTab(t.id)}
                    className={`relative flex items-center gap-3.5 rounded-xl px-4 py-3.5 text-left transition-colors ${
                      isActive ? "bg-white/10" : "hover:bg-white/5"
                    }`}
                  >
                    {isActive && (
                      <span
                        className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-full"
                        style={{ background: `linear-gradient(180deg, ${from}, ${to})` }}
                      />
                    )}
                    <Icon
                      className="h-6 w-6 shrink-0"
                      style={{ color: isActive ? from : "rgba(255,255,255,0.5)" }}
                    />
                    <span
                      className={`text-xl font-medium ${
                        isActive ? "text-white" : "text-white/60"
                      }`}
                    >
                      {t.label}
                    </span>
                    <span className="ml-auto rounded-md border border-white/15 bg-white/5 px-2 font-mono text-xl text-white/45">
                      {i + 1}
                    </span>
                  </button>
                );
              })}
            </nav>

            {/* Report area */}
            <main className="relative min-h-0 flex-1 overflow-hidden p-5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tab}
                  className="h-full"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
                >
                  {tab === "performance" && <PerformanceTab {...pinProps} />}
                  {tab === "indexing" && <IndexingTab {...pinProps} />}
                  {tab === "vitals" && <VitalsTab {...pinProps} />}
                  {tab === "sitemaps" && <SitemapsTab {...pinProps} />}
                </motion.div>
              </AnimatePresence>
            </main>
          </div>

          {/* Closing beat — persistent on every tab */}
          <div className="shrink-0 border-t border-white/10 px-8 py-3.5 text-center text-xl text-white/80">
            Impressions with weak positions are your content roadmap —{" "}
            <span className="font-medium text-white/90">
              GSC hands it to you for free.
            </span>
          </div>
        </div>
      </Reveal>

      <ExplainerPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        eyebrow={callout?.eyebrow}
        title={callout?.title ?? ""}
      >
        {callout?.body}
      </ExplainerPanel>
    </div>
  );
}

function GscDemo({ active }: SlideProps) {
  return (
    <SlideShell chapter={5} kicker="Demo · Google Search Console">
      <GscBody active={active} />
    </SlideShell>
  );
}

export const gscDemo: SlideDef = {
  id: "c5-gsc-demo",
  chapter: 5,
  title: "Demo: Long-tail ideas in Search Console",
  component: GscDemo,
};
